import { createContext, useContext, useEffect, useRef, useState } from "react";
import socket from "./socket";

import { AppLog } from "services";

interface SocketContextProps {
  isConnected: boolean;
  joinedChannels: string[];
  joinRoom: (data: string) => void;
  leaveRoom: (data: string) => void;
}

const SocketContext = createContext<SocketContextProps | undefined>(undefined);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const joinedChannelsRef = useRef<Set<string>>(new Set());
  const [joinedChannels, setJoinedChannels] = useState<string[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const handleConnect = () => {
      setIsConnected(true);
      const roomsToRejoin = Array.from(joinedChannelsRef.current);

      roomsToRejoin.forEach((roomId) => {
        socket.emit("onJoinFlipFlopRoom", roomId, (response: { status: string; message: string }) => {
          if (response.status === "success") {
            AppLog.log(`Rejoined room: ${roomId}`);
          } else {
            AppLog.log(`Failed to rejoin ${roomId}: ${response.message}`);
          }
        });
      });

      setJoinedChannels(roomsToRejoin);
    };

    const handleDisconnect = () => {
      setIsConnected(false);
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
    };
  }, []);

  const joinRoom = (roomId: string) => {
    if (!joinedChannelsRef.current.has(roomId)) {
      joinedChannelsRef.current.add(roomId);
      setJoinedChannels(Array.from(joinedChannelsRef.current));

      socket.emit("onJoinFlipFlopRoom", roomId, (response: { status: string; message: string }) => {
        if (response.status === "success") {
          // AppLog({ message: `Joined room: ${roomId}` });
        } else {
          AppLog.log(`Failed to join ${roomId}: ${response.message}`);
        }
      });
    }
  };

  const leaveRoom = (roomId: string) => {
    if (joinedChannelsRef.current.has(roomId)) {
      joinedChannelsRef.current.delete(roomId);
      setJoinedChannels(Array.from(joinedChannelsRef.current));

      socket.emit("onLeaveFlipFlopRoom", roomId, (response: { status: string; message: string }) => {
        if (response.status === "success") {
          // AppLog({ message: `Left room: ${roomId}` });
        } else {
          AppLog.log(`Failed to leave ${roomId}: ${response.message}`);
        }
      });
    } else {
      AppLog.log(`Attempted to leave a room not joined: ${roomId}`);
    }
  };

  return <SocketContext.Provider value={{ isConnected, joinedChannels, joinRoom, leaveRoom }}>{children}</SocketContext.Provider>;
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};
